import { forwardRef, useMemo, useState } from 'react'
import { getShapeFromElement } from '@editorUtils'
import {
  Assignment,
  CreateNewFolder,
  Description,
  ImportContacts,
  Link,
  Segment,
  SimCardAlert,
  Toc,
  UploadFile,
} from '@mui/icons-material'
import DeleteIcon from '@mui/icons-material/Delete'
import FolderRounded from '@mui/icons-material/FolderRounded'
import { CircularProgress, Divider, Stack } from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { styled, alpha } from '@mui/material/styles'
import {
  TreeItemLabel,
  UseTreeItemParameters,
  useTreeItem,
  useTreeItemModel,
  TreeItemProvider,
  TreeItemIconContainer,
  TreeItemIcon,
  TreeItemDragAndDropOverlay,
  TreeItemGroupTransition,
} from '@mui/x-tree-view-pro'
import { ContextMenu } from '@ui/actions'
import { TooltipIconButton } from '@ui/feedback'
import { useResyncFile } from '@editorHooks'
import { useDocumentTreeContext } from '../../DocumentTreeContext'
import DeleteProgress from './components/DeleteProgress'
import DiffModal from './components/DiffModal'
import { getElementDataDiff } from './utils'

type ItemType = 'file' | 'position' | 'folder' | 'toc' | 'cover-page' | 'full-doc'

type ExtendedTreeItemProps = {
  itemType: ItemType
  id: string
  label: string
  onDelete?: (id: string) => void
  onCreateFolder?: () => void
  onUploadDocument?: () => void
  level: string
  onContext?: () => void
  disableDelete?: boolean
  isTemplate?: boolean
  isGeneratingTemplate?: boolean
  disableActions?: boolean
  item: FileItem | SectionItem
}

declare module 'react' {
  interface CSSProperties {
    '--tree-view-color'?: string
    '--tree-view-bg-color'?: string
  }
}

const TreeItemRoot = styled('li')(({ theme }) => ({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  outline: 0,
  color: theme.palette.grey[400],
  ...theme.applyStyles('light', {
    color: theme.palette.grey[800],
  }),
}))

const TreeItemContent = styled('div')<{ hasDiff?: boolean }>(({ theme, hasDiff }) => ({
  padding: theme.spacing(0.125),
  paddingRight: theme.spacing(0.5),
  paddingLeft: `calc(${theme.spacing(
    0.5,
  )} + var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth))`,
  width: '100%',
  boxSizing: 'border-box',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  borderRadius: theme.spacing(0.25),
  marginBottom: theme.spacing(0.125),
  marginTop: theme.spacing(0.125),
  fontWeight: 500,
  ...(hasDiff && {
    backgroundColor: theme.palette.warning.light,
    '&:hover': {
      backgroundColor: theme.palette.warning.main,
    },
    '&[data-selected]': {
      backgroundColor: theme.palette.warning.dark,
      color: theme.palette.warning.contrastText,
    },
    '&[data-focused]:not([data-selected])': {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.warning.contrastText,
    },
  }),
  '&[data-expanded]:not([data-focused], [data-selected]) .labelIcon': {
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      left: '16px',
      top: '44px',
      height: 'calc(100% - 48px)',
      width: '1.5px',
      backgroundColor: theme.palette.grey[700],
      ...theme.applyStyles('light', {
        backgroundColor: theme.palette.grey[300],
      }),
    },
  },
  [`&[data-selected]`]: {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
    ...theme.applyStyles('light', {
      backgroundColor: theme.palette.primary.main,
    }),
  },
  [`&[data-focused]:not([data-selected])`]: {
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
    color: theme.palette.primary.contrastText,
    ...theme.applyStyles('light', {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main,
    }),
  },
  '&:not([data-focused], [data-selected]):hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: 'white',
    ...theme.applyStyles('light', {
      color: theme.palette.primary.main,
    }),
  },
}))

const TreeItemLabelText = styled(Typography)({
  color: 'inherit',
})

interface CustomLabelProps {
  children: React.ReactNode
  icon?: React.ElementType
  expandable?: boolean
}

function CustomLabel({ icon: Icon, expandable, children, ...other }: CustomLabelProps) {
  return (
    <TreeItemLabel
      {...other}
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
      }}
    >
      {Icon && (
        <Box
          component={Icon}
          className="labelIcon"
          color="inherit"
          sx={{ mr: 1, fontSize: '1.2rem' }}
        />
      )}

      <TreeItemLabelText variant="body2">{children}</TreeItemLabelText>
    </TreeItemLabel>
  )
}

const getIconFromFileType = (fileType: ItemType) => {
  switch (fileType) {
    case 'position':
      return Link
    case 'file':
      return Description
    case 'folder':
      return Segment
    case 'toc':
      return Toc
    case 'cover-page':
      return ImportContacts
    case 'full-doc':
      return Assignment
    default:
      return Description
  }
}

interface Props
  extends Omit<UseTreeItemParameters, 'rootRef'>,
    Omit<React.HTMLAttributes<HTMLLIElement>, 'onFocus'> {}

const CustomTreeItem = forwardRef(function CustomTreeItem(
  props: Props,
  ref: React.Ref<HTMLLIElement>,
) {
  const { id, itemId, label, disabled, children, ...other } = props

  const {
    getContextProviderProps,
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getLabelProps,
    getGroupTransitionProps,
    getDragAndDropOverlayProps,
    status,
  } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref })

  const item = useTreeItemModel<ExtendedTreeItemProps>(itemId)!

  let icon
  if (item.itemType) {
    icon = getIconFromFileType(item.itemType)
  } else if (status.expandable) {
    icon = FolderRounded
  }

  const {
    guidToElement,
    guidToCrossSection,
    deleteDocument: deleteDoc,
    deletingDocumentId,
    deleteFolder,
    deletingFolderId,
    isDeleteBlocked,
  } = useDocumentTreeContext()

  const isDeleting = deletingDocumentId === itemId
  const isDeletingFolder = deletingFolderId === itemId

  const contextItems = useMemo(() => {
    if (item.itemType === 'folder' && item.onCreateFolder && item.onUploadDocument) {
      return [
        {
          label: 'Abschnitt hinzufügen',
          onClick: item.onCreateFolder,
          icon: <CreateNewFolder />,
        },
        {
          label: 'Dokument hochladen',
          onClick: item.onUploadDocument,
          icon: <UploadFile />,
        },
        {
          label: 'Löschen',
          onClick: () => deleteFolder(itemId),
          icon: <DeleteIcon />,
          disabled: item.disableDelete || isDeleteBlocked,
        },
      ]
    }

    if (
      item.itemType === 'file' ||
      item.itemType === 'position' ||
      item.itemType === 'toc' ||
      item.itemType === 'cover-page' ||
      item.itemType === 'full-doc'
    ) {
      const actions = [
        {
          label: 'Löschen',
          onClick: () => deleteDoc(itemId),
          icon: <DeleteIcon />,
          disabled: isDeleteBlocked,
        },
      ]

      return actions
    }

    return []
  }, [
    deleteDoc,
    deleteFolder,
    item.disableDelete,
    item.itemType,
    item.onCreateFolder,
    item.onUploadDocument,
    itemId,
    isDeleteBlocked,
  ])

  const [dataModalOpen, setDataModalOpen] = useState(false)

  const handleCloseModal = () => {
    setDataModalOpen(false)
  }

  // Default test data if no props provided
  const originalData = useMemo(() => {
    if (item.item.type !== 'file' || !item.item.additional_data) return {}
    if ('_elementData' in item.item.additional_data) return item.item.additional_data._elementData
    return {}
  }, [item.item.additional_data, item.item.type])

  const newData = useMemo(
    () =>
      item.item.type === 'file' && item.item.element_guid
        ? {
            shape: getShapeFromElement(guidToElement[item.item.element_guid]),
            crossSection: guidToCrossSection[item.item.element_guid],
          }
        : {},
    [guidToElement, guidToCrossSection, item.item],
  )

  const diff = useMemo(() => getElementDataDiff(item.item, newData), [item.item, newData])

  const { mutateResyncFile, isMutatingResyncFile } = useResyncFile(
    () => {
      setDataModalOpen(false)
    },
    guidToElement,
    guidToCrossSection,
  )

  return (
    <>
      <TreeItemProvider {...getContextProviderProps()}>
        <TreeItemRoot {...getRootProps(other)}>
          <ContextMenu
            items={contextItems}
            onContext={item.onContext}
            disabled={item.disableActions}
          >
            <TreeItemContent
              {...getContentProps()}
              hasDiff={!!diff}
              data-cy="document-tree-item"
              // Marked (orange) when the element changed since the item's snapshot
              data-outdated={diff ? 'true' : 'false'}
              sx={{ justifyContent: 'space-between' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TreeItemIconContainer {...getIconContainerProps()}>
                  <TreeItemIcon status={status} />
                </TreeItemIconContainer>

                <CustomLabel
                  {...getLabelProps({
                    icon,
                    expandable: status.expandable && status.expanded,
                    style: {
                      textDecoration: !item.isTemplate ? 'underline' : 'none',
                    },
                  })}
                />
              </Box>
              <Stack direction="row" spacing={4}>
                <Stack direction="row">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      width: 80,
                    }}
                  >
                    <Typography variant="body2">{item.level}</Typography>
                  </Box>

                  <Divider orientation="vertical" flexItem />
                </Stack>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    width: 120,
                  }}
                  pl={2}
                >
                  <TreeItemDragAndDropOverlay {...getDragAndDropOverlayProps()} />
                  {(item.itemType === 'file' ||
                    item.itemType === 'position' ||
                    item.itemType === 'toc' ||
                    item.itemType === 'cover-page' ||
                    item.itemType === 'full-doc') && (
                    <Stack direction="row" spacing={0.5}>
                      {/* The button stays while deleting so the row keeps its height */}
                      <TooltipIconButton
                        size="small"
                        tooltip="Löschen"
                        aria-label="Löschen"
                        onClick={e => {
                          e.stopPropagation()
                          deleteDoc(itemId)
                        }}
                        {...getLabelProps()}
                        sx={{ color: 'inherit' }}
                        disabled={item.disableActions || isDeleteBlocked || isDeleting}
                      >
                        {isDeleting ? <DeleteProgress /> : <DeleteIcon fontSize="small" />}
                      </TooltipIconButton>
                      {item.isGeneratingTemplate && (
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <CircularProgress
                            size={16}
                            {...getLabelProps()}
                            sx={{ color: 'inherit' }}
                          />
                        </Box>
                      )}

                      {diff && (
                        <TooltipIconButton
                          size="small"
                          tooltip="Elementdaten vergleichen"
                          aria-label="Elementdaten vergleichen"
                          sx={{ color: 'inherit' }}
                          onClick={() => setDataModalOpen(true)}
                        >
                          <SimCardAlert fontSize="small" />
                        </TooltipIconButton>
                      )}
                    </Stack>
                  )}

                  {item.itemType === 'folder' && (
                    <>
                      {item.onCreateFolder && (
                        <TooltipIconButton
                          size="small"
                          tooltip="Abschnitt hinzufügen"
                          aria-label="Abschnitt hinzufügen"
                          onClick={e => {
                            e.stopPropagation()
                            item.onCreateFolder?.()
                          }}
                          {...getLabelProps()}
                          sx={{ color: 'inherit' }}
                          disabled={item.disableActions}
                        >
                          <CreateNewFolder fontSize="small" />
                        </TooltipIconButton>
                      )}

                      {item.onUploadDocument && (
                        <TooltipIconButton
                          size="small"
                          tooltip="Dokument hochladen"
                          aria-label="Dokument hochladen"
                          onClick={e => {
                            e.stopPropagation()
                            item.onUploadDocument?.()
                          }}
                          {...getLabelProps()}
                          sx={{ color: 'inherit' }}
                          disabled={item.disableActions}
                        >
                          <UploadFile fontSize="small" />
                        </TooltipIconButton>
                      )}

                      <TooltipIconButton
                        size="small"
                        tooltip="Löschen"
                        aria-label="Löschen"
                        onClick={e => {
                          e.stopPropagation()
                          deleteFolder(itemId)
                        }}
                        {...getLabelProps()}
                        sx={{ color: 'inherit' }}
                        disabled={item.disableDelete || item.disableActions || isDeleteBlocked}
                      >
                        {isDeletingFolder ? <DeleteProgress /> : <DeleteIcon fontSize="small" />}
                      </TooltipIconButton>
                    </>
                  )}
                </Box>
              </Stack>
            </TreeItemContent>
          </ContextMenu>
          {children && <TreeItemGroupTransition {...getGroupTransitionProps()} />}
        </TreeItemRoot>
      </TreeItemProvider>

      {diff && (
        <DiffModal
          open={dataModalOpen}
          onClose={handleCloseModal}
          onResync={() => mutateResyncFile(item.item.guid)}
          originalData={originalData}
          newData={newData}
          isResyncingFile={isMutatingResyncFile}
        />
      )}
    </>
  )
})

export default CustomTreeItem
